import React from 'react';
import { Grid, TextField, Autocomplete, useTheme, alpha } from '@mui/material';
import { RevisionName } from '../../types';
import { RevisionFormValues } from './formTypes';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

export interface RevisionFormProps {
  value: RevisionFormValues;
  onChange: (value: RevisionFormValues) => void;
  revisionNames: RevisionName[];
  nextRevisionCounter?: number;
  lockIdentity?: boolean;
  lockDates?: boolean;
  invalid?: (key: string) => boolean;
  onFieldEdit?: (key: string) => void;
}

const RevisionForm: React.FC<RevisionFormProps> = ({
  value,
  onChange,
  revisionNames,
  nextRevisionCounter,
  lockIdentity = false,
  lockDates = false,
  invalid,
  onFieldEdit,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const controlBg = isDark ? alpha('#0a1622', 0.6) : alpha(NAUTILUS.deepOcean, 0.03);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      bgcolor: controlBg,
      '& fieldset': { borderColor: panelBorder },
      '&:hover fieldset': {
        borderColor: isDark ? alpha(NAUTILUS.sand, 0.35) : alpha(NAUTILUS.deepOcean, 0.25),
      },
      '&.Mui-focused fieldset': { borderColor: NAUTILUS.sand },
      '&.Mui-error fieldset': { borderColor: theme.palette.error.main },
      '&.Mui-error:hover fieldset': { borderColor: theme.palette.error.main },
      '&.Mui-error.Mui-focused fieldset': { borderColor: theme.palette.error.main },
    },
  };

  const patch = (partial: Partial<RevisionFormValues>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={revisionNames}
          getOptionLabel={(option) => option.name}
          value={revisionNames.find((rn) => rn.id === value.revision_name_id) || null}
          onChange={(_, newValue) => {
            onFieldEdit?.('revision_name_id');
            patch({ revision_name_id: newValue?.id || 0 });
          }}
          disabled={lockIdentity}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Revision Name"
              required
              size="small"
              error={Boolean(invalid?.('revision_name_id'))}
              sx={textFieldSx}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          required
          label="Step"
          type="number"
          value={value.step}
          error={Boolean(invalid?.('step'))}
          onChange={(e) => {
            onFieldEdit?.('step');
            patch({ step: parseInt(e.target.value, 10) || 1 });
          }}
          disabled={lockIdentity}
          InputProps={{ inputProps: { min: 1, max: 20 } }}
          helperText="Supported revisions from 1 to 20."
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Revision Counter"
          value={nextRevisionCounter ?? '-'}
          InputProps={{ readOnly: true }}
          helperText="Auto-assigned counter for this revision"
          disabled
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Start Date"
          type="date"
          value={value.start_date || ''}
          onChange={(e) => patch({ start_date: e.target.value })}
          disabled={lockDates}
          InputLabelProps={{ shrink: true }}
          helperText={lockDates ? 'Locked after author upload.' : undefined}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Expected Issue Date"
          type="date"
          value={value.expected_issue_date}
          onChange={(e) => patch({ expected_issue_date: e.target.value })}
          disabled={lockDates}
          InputLabelProps={{ shrink: true }}
          helperText={lockDates ? 'Locked after author upload.' : undefined}
          sx={textFieldSx}
        />
      </Grid>
    </Grid>
  );
};

export default RevisionForm;
